import { BaseModel } from "./BaseModel";
import { IBetData } from "../server/data/IBetData";
import { ICurrency, ICurrencyFormatConf } from "./ICurrency";
import App from "../App";

class BetModel extends BaseModel<IBetData> {
    public balance: number = 0;
    public balanceAtStart: number = 0;
    public currency: ICurrency = {
        ISO: "EUR",
        symbol: "€",
        decimals: 2
    };
    public isDisplayingCredits: boolean = false;
    public defaultCurrencyConfig: ICurrencyFormatConf = {
        showIdentifier: false,
        showSymbol: false,
        prependSymbol: false,
        identifierSpaceCount: 0
    };
    public creditValues: number[] = [];
    public denomination: number = 1;
    public minLinesAllowed: number = 0;
    public maxLinesAllowed: number = 0;
    public coinSizeIndex: number = 0;
    public coinSize: number = 0;
    public numLines: number = 0;
    public currentBetMultiplier: number = 0;
    public betMultipliers: number[] = [];
    public betMultiplierMin: number = 0;
    public betMultiplierMax: number = 0;
    public totalBet: number = 0;
    public betTime: number = 0;
    public totalBetSteps: number = 20;
    public insufficientBalance: boolean = false;
    public betStatesData: { [bet: number]: {[propName:string]:any} } = {};

    protected allPossibleBets: { coinIndex: number, multiplier: number, value: number }[] = [];


    constructor() {
        super();
        //this.coinSizeIndex = 0;
    }

    public getCurrentBalance(): number {
        return this.balanceAtStart || this.balance;
    }

    public clearStartBalance() {
        this.balanceAtStart = 0 ;
    }

    public getPossibleBetByIndex(index:number) {
        if(!this.allPossibleBets[index]) {
            return null;
        }

        let betData = this.allPossibleBets[index];

        // return this.creditValues[betData.coinIndex]*betData.multiplier*betData.value*this.numLines;
        return betData;

    }

    // because it is not known in what format the currency comes from server for different vendors it should be set at a given time
    public setCurrency(currency: ICurrency, currencyConfig?: ICurrencyFormatConf) {
        this.currency = currency;
        if (currencyConfig) {
            this.defaultCurrencyConfig = currencyConfig;
        }
    }

    public subtractBet(bet: number): boolean {
        if (this.balance < bet) {
            return false;
        } else {
            this.balance -= bet;
            return true;
        }
    }

    public formatCurrency(value: number, currencyFormatConf: ICurrencyFormatConf = this.defaultCurrencyConfig): string {
        let result;
        let valueString;
        let currencyName = "";

        if (this.isDisplayingCredits) {
            valueString = value * this.denomination;
        } else {
            valueString = value / App.config.subUnits;
        }

        if (currencyFormatConf.showIdentifier) {
            currencyName = currencyFormatConf.showSymbol ? this.currency.symbol : this.currency.ISO
        }

        if (currencyFormatConf.prependSymbol) {
            result = currencyName + " ".repeat(currencyFormatConf.identifierSpaceCount) + valueString;
        } else {
            result = valueString + " ".repeat(currencyFormatConf.identifierSpaceCount) + currencyName;
        }

        return result;
    }

    public update(data) {
        super.update(data);
        this.recalcBet();
    }

    public getMaxBet() {
        return this.betMultiplierMax;
    }

    public getMaxCoinIndex() {
        return this.creditValues.length - 1;
    }

    public getMaxPossibleBet() {
        let maxCoinIndex = this.getMaxCoinIndex();
        for (let i = maxCoinIndex; i >= 0; i--) {
            if (this.balance >= this.creditValues[i]) {
                return i;
            }
        }
        return 0;
    }

    public setStake(bet?: number, coinIndex?: number) {
        if (bet) {
            this.currentBetMultiplier = bet;
        }
        if (this.creditValues[coinIndex]) {
            this.coinSizeIndex = coinIndex;
            this.coinSize = this.creditValues[coinIndex];
        }
        this.recalcBet();
    }

    public getCurrentBetStateData() {
        if(this.betStatesData[this.totalBet]) {

            return this.betStatesData[this.totalBet];
        } else {
            return { state: "empty"}
        }
    }

    /**
     * Cycle through bet multiplier values
     * @public
     * @param dir {Number} - should be either 1 or -1 for cycling upwards / downwards
     */
    public cycleBetMultiplier(dir = 1) {
        this.currentBetMultiplier += dir;
        if (this.currentBetMultiplier > this.betMultiplierMax) {
            this.currentBetMultiplier = this.betMultiplierMax;
        } else if (this.currentBetMultiplier <= 0) {
            this.currentBetMultiplier = this.betMultiplierMin;
        }
        this.recalcBet();
        // App.dispatcher.emit(GameEvents.UI_UPDATE);
    }

    /**
     * Cycle through number of lines
     * @public
     * @param dir {Number} - should be either 1 or -1 for cycling upwards / downwards
     */
    public cycleNumLines(dir = 1) {
        this.numLines += dir;
        if (this.numLines > this.minLinesAllowed) {
            this.numLines = this.minLinesAllowed;
        } else if (this.numLines == 0) {
            this.numLines = this.minLinesAllowed;
        }

        this.recalcBet();
    }

    /**
     * Cycle through coin size
     * @public
     * @param dir {Number} - should be either 1 or -1 for cycling upwards / downwards
     */
    public cycleCoinSize(dir = 1) {
        this.coinSizeIndex = this.coinSizeIndex + dir;
        if (this.coinSizeIndex >= this.creditValues.length) {
            this.coinSizeIndex = this.creditValues.length - 1;
        }

        if (this.coinSizeIndex <= 0) {
            this.coinSizeIndex = 0;
        }
        this.coinSize = this.creditValues[this.coinSizeIndex];
        this.recalcBet();
        // App.dispatcher.emit(GameEvents.UI_UPDATE);
    }

    protected recalcBet() {
        this.totalBet = this.coinSize * this.currentBetMultiplier * this.numLines;
    }

    public setInitialParams() {
        //this.coinSizeIndex = 0; // todo check if different value comes on resume
        this.coinSize = this.creditValues[this.coinSizeIndex];
        this.betMultiplierMin = this.betMultipliers[0];
        this.betMultiplierMax = this.betMultipliers[this.betMultipliers.length - 1];
        this.currentBetMultiplier =
        this.currentBetMultiplier > 0 ? this.currentBetMultiplier : this.betMultiplierMin;

        this.recalcBet();
    }

    public cycleTotalBet(dir = 1) {
        let currentPos = this.allPossibleBets.findIndex(el => el.value == this.totalBet / this.numLines);
        let nextPos = currentPos + dir * Math.floor(this.allPossibleBets.length / this.totalBetSteps);
        if (nextPos < 0) {
            nextPos = 0;
        }
        if (nextPos >= this.allPossibleBets.length) {
            nextPos = this.allPossibleBets.length - 1;
        }
        this.coinSizeIndex = this.allPossibleBets[nextPos].coinIndex;
        this.coinSize = this.creditValues[this.coinSizeIndex];
        this.currentBetMultiplier = this.allPossibleBets[nextPos].multiplier;
        this.recalcBet();
    }
}

export default new BetModel();
